import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../api";
import { PageTitle, TH, TD, Badge } from "./AdminLayout";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const PRIORITY_TONES = { high: "red", medium: "amber", low: "grey" };

export default function Tickets() {
  const [tickets, setTickets] = useState([]);
  const [filter, setFilter] = useState("open");
  const [active, setActive] = useState(null);
  const [note, setNote] = useState("");

  const load = () => api.get(`/admin/tickets${filter ? `?status=${filter}` : ""}`).then((r) => setTickets(r.data)).catch(() => {});
  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const patch = async (id, body) => {
    try {
      await api.patch(`/admin/tickets/${id}`, body);
      toast.success("Ticket updated");
      setNote("");
      load();
      if (active?.id === id) {
        const res = await api.get(`/admin/tickets`);
        setActive(res.data.find((t) => t.id === id) || null);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="admin-tickets">
      <PageTitle title="Support & Reports" sub="Tickets submitted from the public Support page." />
      <div className="flex gap-2 mb-6" data-testid="tickets-filter">
        {["open", "closed", ""].map((s) => (
          <button key={s || "all"} onClick={() => setFilter(s)} data-testid={`tickets-filter-${s || "all"}`}
            className={`px-4 py-2 rounded-full text-sm font-semibold capitalize transition-colors duration-150 ${filter === s ? "bg-blurple text-white" : "border border-white/15 text-white/50 hover:text-white"}`}>
            {s || "All"}
          </button>
        ))}
      </div>

      <div className="rounded-lg border border-white/5 bg-panel overflow-x-auto">
        <table className="w-full min-w-[760px]" data-testid="tickets-table">
          <thead className="border-b border-white/5"><tr><TH>Subject</TH><TH>Type</TH><TH>From</TH><TH>Priority</TH><TH>Status</TH><TH>Created</TH><TH /></tr></thead>
          <tbody className="divide-y divide-white/5">
            {tickets.map((t) => (
              <tr key={t.id} className="hover:bg-white/[0.02] cursor-pointer" onClick={() => setActive(t)} data-testid={`ticket-row-${t.id}`}>
                <TD className="text-white font-medium max-w-[260px]"><span className="line-clamp-1">{t.subject}</span></TD>
                <TD><Badge tone="blue">{t.type}</Badge></TD>
                <TD>{t.name}<span className="block text-xs text-white/35">{t.email}</span></TD>
                <TD><Badge tone={PRIORITY_TONES[t.priority]}>{t.priority}</Badge></TD>
                <TD><Badge tone={t.status === "open" ? "green" : "grey"}>{t.status}</Badge></TD>
                <TD className="text-xs font-mono text-white/40">{new Date(t.created_at).toLocaleDateString()}</TD>
                <TD className="text-xs text-volt">Open →</TD>
              </tr>
            ))}
            {tickets.length === 0 && <tr><TD className="text-center py-10 text-white/40" colSpan={7}>No tickets here. Enjoy the silence.</TD></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!active} onOpenChange={() => setActive(null)}>
        <DialogContent className="bg-panel border-white/10 text-white max-w-2xl" data-testid="ticket-dialog">
          <DialogHeader><DialogTitle className="font-heading">{active?.subject}</DialogTitle></DialogHeader>
          {active && (
            <div className="space-y-5 mt-1">
              <p className="text-sm text-white/70 leading-relaxed rounded-lg bg-white/[0.03] border border-white/5 p-4">{active.message}</p>
              <div className="flex gap-3 flex-wrap">
                <select value={active.status} onChange={(e) => patch(active.id, { status: e.target.value })} data-testid="ticket-status-select"
                  className="bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:outline-none [&>option]:bg-panel">
                  <option value="open">Open</option>
                  <option value="closed">Closed</option>
                </select>
                <select value={active.priority} onChange={(e) => patch(active.id, { priority: e.target.value })} data-testid="ticket-priority-select"
                  className="bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:outline-none [&>option]:bg-panel">
                  <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
                </select>
                <input defaultValue={active.assignee} placeholder="Assign to (email)" onBlur={(e) => e.target.value !== active.assignee && patch(active.id, { assignee: e.target.value })} data-testid="ticket-assign-input"
                  className="bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blurple" />
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.15em] text-white/40 font-semibold mb-2">Internal notes</p>
                <div className="space-y-2 mb-3 max-h-40 overflow-y-auto">
                  {(active.notes || []).map((n, i) => (
                    <p key={i} className="text-xs text-white/60 border-l-2 border-blurple/40 pl-3"><span className="text-volt font-mono">{n.by}</span> — {n.text}</p>
                  ))}
                  {!(active.notes || []).length && <p className="text-xs text-white/30">No notes yet.</p>}
                </div>
                <div className="flex gap-2">
                  <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add an internal note…" data-testid="ticket-note-input"
                    className="flex-1 bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blurple" />
                  <button onClick={() => note.trim() && patch(active.id, { note: note.trim() })} data-testid="ticket-note-add"
                    className="bg-blurple hover:bg-[#4752c4] text-white text-sm font-semibold px-5 rounded-md transition-colors duration-150">Add</button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
